<script setup lang="ts">
  import type { QTableProps } from 'quasar'

  import { useQuery } from '@tanstack/vue-query'

  import { type OfficeOut, listOfficesPassportIdmIdmPrivateListOfficesPost } from '@/shared/api/intranet'
  import { UButton } from '@/shared/ui/components'
  import { PencilIcon, PlusIcon } from '@/shared/ui/svg'
  definePage({
    meta: {
      layout: 'admin',
    },
  })
  const router = useRouter()

  const searchQuery = ref('')
  const handleQueryChange = (value: string | null) => {
    if (value === null) {
      searchQuery.value = ''
    } else {
      searchQuery.value = value
    }
  }

  const { data: offices, isLoading } = useQuery({
    queryKey: ['offices'],
    queryFn: () => {
      return listOfficesPassportIdmIdmPrivateListOfficesPost({
        body: { filter: {} },
        throwOnError: true,
      })
    },
    select: (response) => response.data.offices,
  })

  const filteredOffices = computed(() => {
    return offices.value?.filter((office) => {
      return office.name.toLowerCase().includes(searchQuery.value.toLowerCase())
    })
  })

  const tableConfig: QTableProps['columns'] = [
    {
      name: 'name',
      label: 'admin_offices_table_name',
      align: 'left',
      field: (row: OfficeOut) => {
        return row.name
      },
    },
    {
      name: 'actions',
      label: 'admin_offices_table_actions',
      align: 'right',
      field: (row: OfficeOut) => {
        return row.id
      },
    },
  ]

  const edit = (id: string) => {
    router.push({ name: '/admin/offices/[id]/edit', params: { id } })
  }
</script>

<template>
  <div class="flex h-full w-full flex-col gap-4">
    <div class="head">
      <div class="content flex items-center justify-between">
        <h2>{{ $t('admin_offices_title') }}</h2>
        <UButton
          class="flex items-center gap-2 px-2"
          size="small"
          type="secondary"
          @click="$router.push('/admin/offices/new')"
        >
          <PlusIcon
            width="12"
            height="12"
          />
          {{ $t('admin_offices_add_office') }}
        </UButton>
      </div>
    </div>
    <QInput
      :model-value="searchQuery"
      class="mb-4 flex-1"
      :placeholder="$t('admin_offices_search_placeholder')"
      outlined
      clearable
      dense
      @update:model-value="(value) => handleQueryChange(value as string | null)"
    />
    <QTable
      :columns="tableConfig"
      :rows="filteredOffices || []"
      hide-pagination
      :rows-per-page-options="[0]"
      :no-data-label="$t('admin_offices_no_data_available')"
      :loading="isLoading"
    >
      <template #loading>
        <QInnerLoading
          showing
          color="primary"
        />
      </template>
      <template #header-cell="headCellProps">
        <QTh :props="headCellProps">
          {{ $t(headCellProps.col.label) }}
        </QTh>
      </template>
      <template #body-cell-name="cellProps">
        <QTd>
          <div class="flex cursor-pointer items-center">
            <div>{{ cellProps.value }}</div>
          </div>
        </QTd>
      </template>
      <template #header-cell-actions="headCellProps">
        <QTh :props="headCellProps">
          <span />
        </QTh>
      </template>
      <template #body-cell-actions="cellProps">
        <QTd>
          <div class="flex items-center justify-end">
            <PencilIcon
              class="cursor-pointer"
              @click="edit(cellProps.value)"
            />
          </div>
        </QTd>
      </template>
    </QTable>
  </div>
</template>

<style lang="scss" scoped>
  .q-table {
    @include font-montserrat-400(14px, 24px, var(--dark-black-2));
    max-height: 768px;
    min-height: 150px;
    :deep(th) {
      font-weight: 600;
    }
  }
  :deep(.q-table__bottom) {
    background-color: var(--grey);
  }
  :deep(thead) {
    background-color: var(--grey);
  }
</style>
